import { ApiError, apiFetch } from "../lib/auth";
import { streetViewImageUrl } from "./api";
import { photoToDataUri, prepareSubjectPhoto } from "./preparePhoto";

/**
 * THE SUBJECT PHOTO OPTIONS (owner spec, 2026-11-02): every photo available for the subject — each
 * MLS photo labeled with its MLS number, plus Street View with its capture date — and the agent
 * picks. The default is the listing's first MLS photo.
 *
 * CONTRACT (the server route is PR #121, aafecb6; this PR is client-only and aligned to it):
 *
 *   GET /api/subject/photos?job=<id>
 *   200 -> [{ source: "mls" | "streetview", url, label, mls_number, capture_date, is_default }]
 *   MLS entries first ("MLS photo N", url /api/subject/photos/image?job=&n=, mls_number = listing id),
 *   Street View last (mls_number null, capture_date null today). The first entry overall is flagged
 *   default, which is Street View when no MLS entry exists; a user not admitted to MLS gets Street
 *   View only; a job with no subject gets [].
 *
 * Nothing here invents a field. A missing `capture_date` stays missing and the picker says "capture
 * date not stated"; a missing `mls_number` on an MLS photo is shown as "MLS number not stated".
 * When the endpoint is not there (404/405/501) this returns `unavailable` and the caller falls back
 * to today's behavior — the upload control and whatever photo the extract read — and says so.
 */
export interface SubjectPhotoOption {
  source: "mls" | "streetview";
  url: string;
  label: string;
  mls_number?: string | null;
  capture_date?: string | null;
  is_default?: boolean;
}

export type SubjectPhotoOptions =
  /** `fallback` marks the one option built here when #121's route is absent (see `streetViewFallback`). */
  | { kind: "ok"; photos: SubjectPhotoOption[]; fallback?: true }
  /** The route does not exist (yet) or is switched off: today's behavior stands, and the screen says so. */
  | { kind: "unavailable" }
  /** The route exists and failed: a retry is worth offering. */
  | { kind: "error"; message: string };

/** A URL the page may draw and fetch: same-origin path or https. Anything else is dropped, not guessed at. */
function safeUrl(u: unknown): u is string {
  if (typeof u !== "string" || !u) return false;
  if (u.startsWith("//")) return false;
  return u.startsWith("/") || u.startsWith("https://");
}

function clean(raw: unknown): SubjectPhotoOption[] {
  if (!Array.isArray(raw)) return [];
  const out: SubjectPhotoOption[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    if (o.source !== "mls" && o.source !== "streetview") continue;
    if (!safeUrl(o.url)) continue;
    out.push({
      source: o.source,
      url: o.url,
      label: typeof o.label === "string" ? o.label : "",
      mls_number: typeof o.mls_number === "string" && o.mls_number.trim() ? o.mls_number.trim() : null,
      capture_date: typeof o.capture_date === "string" && o.capture_date.trim() ? o.capture_date.trim() : null,
      is_default: o.is_default === true,
    });
  }
  return out;
}

export async function getSubjectPhotoOptions(jobId: string): Promise<SubjectPhotoOptions> {
  try {
    const raw = await apiFetch<unknown>(`/api/subject/photos?job=${encodeURIComponent(jobId)}`);
    return { kind: "ok", photos: clean(raw) };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 405 || e.status === 501)) {
      return { kind: "unavailable" };
    }
    return { kind: "error", message: e instanceof Error ? e.message : "The photo options could not be loaded." };
  }
}

/**
 * WHEN THE OPTIONS ROUTE IS ABSENT (404/405/501, before #121 merges): the one option that main can
 * already serve, built here from the subject address, through main's own proxy route
 * (`GET /api/next/streetview/image?address=`, the same URL helper the reference-photo cell uses).
 * The label and capture-date wording are the ones the server's Street View entry would carry: no date
 * is stated, so none is claimed. No usable address means no option (the caller keeps the plain
 * "not available yet" note). Whether the proxy can actually serve an image (provider off, no
 * coverage) is only known when the image loads; the picker drops to the note if it does not.
 */
export function streetViewFallback(address: string | null | undefined): SubjectPhotoOptions | null {
  const a = (address ?? "").trim();
  if (!a || a === "Subject property") return null;
  return {
    kind: "ok",
    fallback: true,
    photos: [{ source: "streetview", url: streetViewImageUrl(a), label: "Street View", mls_number: null, capture_date: null, is_default: false }],
  };
}

/** A stable identity for one option (the URL is the only field the contract guarantees is unique). */
export function photoKey(p: SubjectPhotoOption): string {
  return `${p.source}:${p.url}`;
}

/**
 * THE DEFAULT RULE (owner: "the default is the MLS front photo"; PR #121 flags the listing's photo 1).
 * The server flags one entry `is_default`: the first MLS photo when there is one, else (today) Street
 * View. This honors the flag for an MLS photo and falls back to the first MLS photo when none is
 * flagged. It NEVER returns Street View: the build cannot credit a picked Street View image (see
 * `STREETVIEW_UNCREDITED`), so Street View is selectable only by the agent's own explicit pick, and
 * with no MLS photo there is no default and nothing is pre-selected.
 */
export function defaultPhoto(photos: SubjectPhotoOption[]): SubjectPhotoOption | null {
  const mls = photos.filter((p) => p.source === "mls");
  return mls.find((p) => p.is_default) ?? mls[0] ?? null;
}

/**
 * Shown in place when the agent picks Street View. A client-supplied `meta.subject_photo` is
 * re-labeled `subject_upload` by `amb_report._confirmed_subject_photo` ("no client can make one read
 * as Street View"), and the Google credit prints only when `subject_photo_source == "streetview"`
 * (`format_pairing.subject_credit`). So a picked Street View image prints as the agent's own with no
 * credit. Said plainly rather than misattributed.
 */
export const STREETVIEW_UNCREDITED = "This picture will be printed without a Street View credit.";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September",
  "October", "November", "December"];

/** "2024-03-18" -> "March 18, 2024"; "2024-03" -> "March 2024"; anything else is shown exactly as stated. */
export function formatCaptureDate(s: string): string {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?(?:T.*)?$/.exec(s);
  if (!m) return s;
  const mon = Number(m[2]);
  if (mon < 1 || mon > 12) return s;
  return m[3] ? `${MONTHS[mon - 1]} ${Number(m[3])}, ${m[1]}` : `${MONTHS[mon - 1]} ${m[1]}`;
}

/** The words that name one option on screen. Pure, so the report and the step say the same thing. */
export function describePhoto(p: SubjectPhotoOption): { title: string; detail: string } {
  if (p.source === "streetview") {
    return {
      title: p.label || "Street View",
      detail: p.capture_date
        ? `Google Street View · captured ${formatCaptureDate(p.capture_date)}`
        : "Google Street View · capture date not stated",
    };
  }
  const num = p.mls_number
    ? (/^mls\b/i.test(p.mls_number) ? p.mls_number : `MLS ${p.mls_number}`)
    : "MLS number not stated";
  return { title: p.label || "MLS photo", detail: num };
}

/**
 * Turn the chosen option into the bounded JPEG data URI the rest of the flow already carries as
 * `meta.subject_photo`. The same gate an upload meets (`prepareSubjectPhoto`) applies; a photo that
 * cannot be fetched or read throws a sentence the picker shows in place.
 */
export async function photoOptionToDataUri(p: SubjectPhotoOption): Promise<string> {
  let blob: Blob;
  try {
    const res = await fetch(p.url, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`status ${res.status}`);
    blob = await res.blob();
  } catch {
    throw new Error("That photo could not be loaded. Choose another, or upload your own.");
  }
  const type = blob.type && blob.type.startsWith("image/") ? blob.type : "image/jpeg";
  const file = new File([blob], "subject-photo", { type });
  const prepared = await prepareSubjectPhoto(file);
  if (!prepared.file) throw new Error(prepared.error ?? "That photo is not one we can print. Choose another.");
  const uri = await photoToDataUri(prepared.file);
  if (!uri) throw new Error("This browser couldn't read that photo. Choose another, or upload your own.");
  return uri;
}
